import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi, humanApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Loader2,
  User,
  Calendar,
  Clock,
  MapPin,
  Briefcase,
  FileText,
  AlertTriangle,
  CheckCircle,
  LogIn,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Download,
  Coffee,
  XCircle,
  Info,
  Phone,
  CreditCard,
} from 'lucide-react';
import { toast } from 'sonner';

const RelatorioPessoaPage = () => {
  const [loading, setLoading] = useState(false);
  const [loadingHumans, setLoadingHumans] = useState(true);
  const [humans, setHumans] = useState([]);
  const [selectedHuman, setSelectedHuman] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [reportData, setReportData] = useState(null);

  const months = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i + 1);

  // Carregar lista de funcionários
  useEffect(() => {
    const loadHumans = async () => {
      try {
        const res = await humanApi.list({ status: 'ativo' });
        // Ordenar por nome
        const sorted = (res.data || []).sort((a, b) => a.name.localeCompare(b.name));
        setHumans(sorted);
      } catch (error) {
        console.error('Erro ao carregar funcionários:', error);
      } finally {
        setLoadingHumans(false);
      }
    };
    loadHumans();
  }, []);

  // Carregar relatório quando selecionar pessoa
  const loadReport = useCallback(async () => {
    if (!selectedHuman) return;
    
    setLoading(true);
    try {
      const res = await reportsApi.getFuncionarioReport(selectedHuman, selectedMonth, selectedYear);
      setReportData(res.data);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      toast.error('Erro ao carregar relatório');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedHuman, selectedMonth, selectedYear]);

  useEffect(() => {
    if (selectedHuman) {
      loadReport();
    }
  }, [selectedHuman, loadReport]);

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}`;
    }
    return dateStr;
  };

  const getWeekday = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    return weekdays[date.getDay()];
  };

  const getLocationBadge = (local) => {
    switch (local) {
      case 'interno':
        return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Interno</Badge>;
      case 'externo':
        return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Externo</Badge>;
      case 'faltou':
        return <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Falta</Badge>;
      case 'folga':
      case 'folga_abonada':
        return <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">Folga</Badge>;
      case 'banco_horas':
        return <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">Banco Hrs</Badge>;
      case 'atestado':
        return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Atestado</Badge>;
      default:
        return <Badge variant="outline">{local}</Badge>;
    }
  };

  const handleExportCSV = () => {
    if (!reportData || !reportData.dias.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const headers = ['Data', 'Dia', 'Entrada', 'Saída', 'Horas', 'Local', 'Local Evento', 'OS', 'Serviço', 'Observação', 'Termo Entrada', 'Termo Saída'];
    const rows = reportData.dias.map(dia => [
      formatDate(dia.data),
      getWeekday(dia.data),
      dia.entrada || '-',
      dia.saida || '-',
      dia.horas || '0',
      dia.local,
      dia.event_location || '-',
      dia.os || '-',
      dia.servico || '-',
      dia.observacao || '-',
      dia.entrada_source === 'manual' ? 'SIM' : '',
      dia.saida_source === 'manual' ? 'SIM' : ''
    ]);

    const csvContent = [
      `Relatório de ${reportData.funcionario.nome} - ${reportData.periodo.mes_nome}/${reportData.periodo.ano}`,
      '',
      headers.join(';'),
      ...rows.map(row => row.join(';')),
      '',
      `Total de Dias: ${reportData.resumo.total_dias_trabalhados}`,
      `Total de Horas: ${reportData.resumo.total_horas}h`,
      `Média por Dia: ${reportData.resumo.media_horas_dia}h`,
      `Total de Termos: ${reportData.resumo.total_termos}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const nomeArquivo = reportData.funcionario.nome.replace(/\s+/g, '_');
    link.setAttribute('download', `relatorio_${nomeArquivo}_${reportData.periodo.mes_nome}_${reportData.periodo.ano}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exportado!');
  };

  if (loadingHumans) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="relatorio-pessoa-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <User className="w-6 h-6 text-blue-500" />
            Relatório por Pessoa
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Histórico completo de escalas e apontamentos por funcionário
          </p>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={selectedHuman || ''} onValueChange={setSelectedHuman}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Selecione a pessoa..." />
            </SelectTrigger>
            <SelectContent>
              {humans.map((h) => (
                <SelectItem key={h.id} value={h.id}>
                  {h.name}
                  {h.contract_type === 'diaria' && (
                    <span className="text-xs text-purple-500 ml-2">(Diária)</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon" onClick={handlePreviousMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.value} value={String(m.value)}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon" onClick={handleNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>

          {reportData && reportData.dias.length > 0 && (
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="flex items-center gap-2 text-green-600 border-green-300 hover:bg-green-50"
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </Button>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      {!selectedHuman ? (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardContent className="py-12 text-center">
            <User className="w-16 h-16 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
            <p className="text-slate-500 dark:text-slate-400">
              Selecione um funcionário para ver o relatório
            </p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : reportData ? (
        <>
          {/* Card do Funcionário */}
          <Card className="dark:bg-slate-800/50 dark:border-slate-700 border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <User className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                      {reportData.funcionario.nome}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={
                        reportData.funcionario.contract_type === 'diaria' 
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      }>
                        {reportData.funcionario.contract_type === 'diaria' ? 'Diária' : 'CLT'}
                      </Badge>
                      <span className="text-sm text-slate-500 dark:text-slate-400 capitalize">
                        {reportData.funcionario.tipo}
                        {reportData.funcionario.subtipo && ` - ${reportData.funcionario.subtipo}`}
                      </span>
                    </div>
                    {reportData.funcionario.phone && (
                      <div className="flex items-center gap-1 mt-1 text-sm text-slate-500">
                        <Phone className="w-3 h-3" />
                        {reportData.funcionario.phone}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {reportData.periodo.mes_nome} {reportData.periodo.ano}
                  </p>
                  {reportData.funcionario.hourly_rate && (
                    <p className="text-xs text-slate-400">
                      Valor hora: R$ {reportData.funcionario.hourly_rate.toFixed(2)}
                    </p>
                  )}
                  {reportData.funcionario.default_daily_rate && (
                    <p className="text-xs text-slate-400">
                      Diária: R$ {reportData.funcionario.default_daily_rate.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cards de Resumo */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            <Card className="dark:bg-slate-800/50 dark:border-slate-700">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {reportData.resumo.total_dias_trabalhados}
                </p>
                <p className="text-xs text-slate-500">Dias Trab.</p>
              </CardContent>
            </Card>

            <Card className="dark:bg-slate-800/50 dark:border-slate-700">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {reportData.resumo.total_horas}h
                </p>
                <p className="text-xs text-slate-500">Total Horas</p>
              </CardContent>
            </Card>

            <Card className="dark:bg-slate-800/50 dark:border-slate-700">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                  {reportData.resumo.media_horas_dia}h
                </p>
                <p className="text-xs text-slate-500">Média/Dia</p>
              </CardContent>
            </Card>

            <Card className={`dark:bg-slate-800/50 dark:border-slate-700 ${reportData.resumo.total_termos > 0 ? 'border-red-200 dark:border-red-800' : ''}`}>
              <CardContent className="p-3 text-center">
                <p className={`text-2xl font-bold ${reportData.resumo.total_termos > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-400'}`}>
                  {reportData.resumo.total_termos}
                </p>
                <p className="text-xs text-slate-500">Ajustes</p>
              </CardContent>
            </Card>

            <Card className={`dark:bg-slate-800/50 dark:border-slate-700 ${reportData.resumo.total_faltas > 0 ? 'border-red-200 dark:border-red-800' : ''}`}>
              <CardContent className="p-3 text-center">
                <p className={`text-2xl font-bold ${reportData.resumo.total_faltas > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-400'}`}>
                  {reportData.resumo.total_faltas}
                </p>
                <p className="text-xs text-slate-500">Faltas</p>
              </CardContent>
            </Card>

            <Card className={`dark:bg-slate-800/50 dark:border-slate-700 ${reportData.resumo.total_atrasos > 0 ? 'border-amber-200 dark:border-amber-800' : ''}`}>
              <CardContent className="p-3 text-center">
                <p className={`text-2xl font-bold ${reportData.resumo.total_atrasos > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400'}`}>
                  {reportData.resumo.total_atrasos}
                </p>
                <p className="text-xs text-slate-500">Atrasos</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabela de Dias */}
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 dark:text-white">
                <Calendar className="w-5 h-5 text-blue-500" />
                Histórico do Mês
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportData.dias.length === 0 ? (
                <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Nenhum registro encontrado no período</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                        <TableHead className="font-semibold w-20">Data</TableHead>
                        <TableHead className="font-semibold text-center">Entrada</TableHead>
                        <TableHead className="font-semibold text-center">Saída</TableHead>
                        <TableHead className="font-semibold text-center">Horas</TableHead>
                        <TableHead className="font-semibold">Local</TableHead>
                        <TableHead className="font-semibold">Local Ev.</TableHead>
                        <TableHead className="font-semibold">OS</TableHead>
                        <TableHead className="font-semibold">Serviço</TableHead>
                        <TableHead className="font-semibold">Observação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.dias.map((dia, idx) => {
                        const hasTermo = dia.entrada_source === 'manual' || dia.saida_source === 'manual';
                        return (
                          <TableRow 
                            key={idx}
                            className={`dark:border-slate-700 ${
                              hasTermo ? 'bg-red-50/50 dark:bg-red-900/10' :
                              dia.local === 'faltou' ? 'bg-red-50 dark:bg-red-900/20' :
                              dia.local === 'folga' || dia.local === 'folga_abonada' ? 'bg-slate-50 dark:bg-slate-800' :
                              ''
                            }`}
                          >
                            <TableCell className="font-medium">
                              <div className="flex flex-col">
                                <span className="text-slate-900 dark:text-white">{formatDate(dia.data)}</span>
                                <span className="text-xs text-slate-400">{getWeekday(dia.data)}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                {dia.entrada_source === 'manual' && (
                                  <AlertTriangle className="w-3 h-3 text-red-500" title="Termo" />
                                )}
                                <span className={`font-mono ${dia.entrada_source === 'manual' ? 'text-red-600 dark:text-red-400' : ''}`}>
                                  {dia.entrada || '-'}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                {dia.saida_source === 'manual' && (
                                  <AlertTriangle className="w-3 h-3 text-red-500" title="Termo" />
                                )}
                                <span className={`font-mono ${dia.saida_source === 'manual' ? 'text-red-600 dark:text-red-400' : ''}`}>
                                  {dia.saida || '-'}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center font-medium">
                              {dia.horas > 0 ? (
                                <span className="text-blue-600 dark:text-blue-400">{dia.horas}h</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {getLocationBadge(dia.local)}
                            </TableCell>
                            <TableCell>
                              {dia.event_location ? (
                                <div className="flex items-center gap-1 text-sm">
                                  <MapPin className="w-3 h-3 text-violet-500" />
                                  <span className="text-violet-700 dark:text-violet-400">{dia.event_location}</span>
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {dia.os ? (
                                <Badge variant="outline" className="bg-slate-100 dark:bg-slate-700">
                                  {dia.os}
                                </Badge>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {dia.servico ? (
                                <Badge className="bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 capitalize">
                                  {dia.servico}
                                </Badge>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="max-w-[200px]">
                              {dia.observacao ? (
                                <div className="text-sm text-slate-600 dark:text-slate-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded border-l-2 border-amber-400">
                                  {dia.observacao}
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pendências */}
          {reportData.pendencias && reportData.pendencias.total > 0 && (
            <Card className="dark:bg-slate-800/50 dark:border-slate-700 border-red-200 dark:border-red-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-red-600 dark:text-red-400">
                  <AlertTriangle className="w-5 h-5" />
                  Pendências ({reportData.pendencias.total})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                    <p className="text-lg font-bold">{reportData.pendencias.ponto}</p>
                    <p className="text-xs text-slate-500">Ponto</p>
                  </div>
                  <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                    <p className="text-lg font-bold">{reportData.pendencias.equipamento}</p>
                    <p className="text-xs text-slate-500">Equipamento</p>
                  </div>
                  <div className="text-center p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
                    <p className="text-lg font-bold text-amber-600">{reportData.pendencias.pendentes_termo}</p>
                    <p className="text-xs text-slate-500">Pend. Termo</p>
                  </div>
                  <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                    <p className="text-lg font-bold text-green-600">{reportData.pendencias.termos_emitidos}</p>
                    <p className="text-xs text-slate-500">Termos Emit.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardContent className="py-12 text-center">
            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-300" />
            <p className="text-slate-500 dark:text-slate-400">
              Erro ao carregar relatório. Tente novamente.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-800 dark:text-blue-300">Dica</p>
              <p className="text-blue-700 dark:text-blue-400 mt-1">
                Linhas com <AlertTriangle className="w-3 h-3 inline text-red-500" /> indicam horário anotado manualmente (termo).<br />
                Use o botão "Exportar CSV" para abrir no Excel e fazer análises detalhadas.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RelatorioPessoaPage;
